import { useEffect } from 'react';
import { Modal, Form, Select, App, theme } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useUpdateAnyCompanyMemberRoleMutation } from '@/api/auth/mutations';
import type { UserWithClientMembership } from '@/api/auth/interfaces';
import type { Company } from '@/api/auth/interfaces';
import { COMPANY_ROLES, TENANT_ROLES, type TenantRoleType } from '@/constants/tenantRoles';
import { useTenantRoleOptions } from '@/hooks/useTenantRoleOptions';
import { ModalTitle } from '@/components/ui/ModalTitle';
import { formModalWidth } from '@/components/ui/formModalStyles';
import { FormModalFooter } from '@/components/ui/FormModalFooter';

export interface AddCompanyMemberModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  clientId: string;
  /** Client members (employees) — only they can be added to a company */
  clientMembers: UserWithClientMembership[];
  /** Companies of this client */
  companies: Company[];
  /** When set, user is fixed (e.g. opened from row "Add to company"); form shows company + role only */
  initialUserId?: string;
}

export function AddCompanyMemberModal({
  open,
  onCancel,
  onSuccess,
  clientId,
  clientMembers,
  companies,
  initialUserId
}: AddCompanyMemberModalProps) {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const updateMutation = useUpdateAnyCompanyMemberRoleMutation(clientId);
  const roleOptions = useTenantRoleOptions(COMPANY_ROLES);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldValue('tenantRole', TENANT_ROLES.EMPLOYEE);
      if (companies.length > 0) {
        form.setFieldValue('companyId', companies[0].id);
      }
    }
  }, [open, form, companies]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const userId = initialUserId ?? values.userId;
      const companyId = values.companyId ?? companies[0]?.id;
      if (!companyId) {
        message.error(t('tenants.detail.addToCompany.selectCompanyRequired', 'Please select a company'));
        return;
      }
      await updateMutation.mutateAsync({
        companyId,
        userId,
        tenantRole: values.tenantRole as TenantRoleType
      });
      message.success(t('tenants.detail.addToCompany.added'));
      form.resetFields();
      onSuccess?.();
      onCancel();
    } catch (e: unknown) {
      const err = e as { errorFields?: unknown[]; message?: string };
      if (!err?.errorFields) {
        message.error(t('tenants.detail.addToCompany.error', { error: err?.message || 'Unknown error' }));
      }
    }
  };

  const userOptions = clientMembers.map((u) => ({
    value: u.id,
    label: u.name ? `${u.name} (${u.email})` : u.email
  }));

  const companyOptions = companies.map((c) => ({ value: c.id, label: c.name }));

  return (
    <Modal
      title={
        <ModalTitle icon={<TeamOutlined />} title={t('tenants.detail.addToCompany.title', 'Add to company')} />
      }
      open={open}
      footer={null}
      onCancel={onCancel}
      destroyOnHidden
      width={formModalWidth(480)}
    >
      {!initialUserId && (
        <p style={{ marginBottom: token.marginMD, color: token.colorTextSecondary }}>
          {t('tenants.detail.addToCompany.descriptionAny', 'Add a client member to a company.')}
        </p>
      )}
      <Form form={form} layout="vertical" initialValues={{ tenantRole: TENANT_ROLES.EMPLOYEE }}>
        {!initialUserId && (
          <Form.Item
            name="userId"
            label={t('tenants.detail.addToCompany.selectUser', 'Client member')}
            rules={[{ required: true, message: t('tenants.detail.addToCompany.selectUserRequired', 'Please select a user') }]}
          >
            <Select
              placeholder={t('tenants.detail.addToCompany.selectUserPlaceholder', 'Select user')}
              showSearch
              optionFilterProp="label"
              options={userOptions}
              notFoundContent={userOptions.length === 0 ? t('tenants.detail.addToCompany.noMembers', 'No client members to add') : null}
            />
          </Form.Item>
        )}
        {companies.length > 0 && (
          <Form.Item
            name="companyId"
            label={t('tenants.detail.addToCompany.selectCompany', 'Company')}
            rules={[{ required: true, message: t('tenants.detail.addToCompany.selectCompanyRequired', 'Please select a company') }]}
          >
            <Select
              placeholder={t('tenants.detail.addToCompany.selectCompanyPlaceholder', 'Select company')}
              options={companyOptions}
              disabled={companies.length === 1}
            />
          </Form.Item>
        )}
        <Form.Item
          name="tenantRole"
          label={t('tenants.detail.addToCompany.role', 'Role')}
          rules={[{ required: true }]}
        >
          <Select options={roleOptions.map(({ value, label }) => ({ value, label }))} />
        </Form.Item>
      </Form>
      <FormModalFooter
        onCancel={onCancel}
        onSubmit={handleSubmit}
        submitLabel={t('tenants.detail.addToCompany.add', 'Add')}
        loading={updateMutation.isPending}
      />
    </Modal>
  );
}
